/**
 * Login screen — email input + "Send magic link" button.
 *
 * Flow:
 * 1. User enters email → sends POST /api/auth/request-magic-link
 * 2. Shows "Check your email" confirmation
 * 3. User clicks magic link in email → handled by AuthVerify component
 *
 * Has a "Skip" button to continue without auth (open mode).
 *
 * Used in two contexts:
 * - From onboarding Welcome screen (fresh install)
 * - From Settings panel (existing user wants to sign in)
 */

import { useState, useCallback } from 'react';
import { getApiUrl } from '../utils/environment';
import { useAuth } from '../auth/AuthContext';
import styles from './Login.module.css';

interface LoginProps {
  /** Called when user chooses to skip authentication */
  onSkip: () => void;
  /** Optional label for the skip button (default: "Continue without account") */
  skipLabel?: string;
}

type LoginStep = 'email' | 'sent' | 'error' | 'verifying';

function getApiBaseUrl(): string {
  return getApiUrl();
}

export function Login({ onSkip, skipLabel = 'Continue without account' }: LoginProps) {
  const [email, setEmail] = useState('');
  const [step, setStep] = useState<LoginStep>('email');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [codeInput, setCodeInput] = useState('');
  const [codeError, setCodeError] = useState<string | null>(null);
  const [codeLoading, setCodeLoading] = useState(false);
  const { login } = useAuth();

  const handleCodeSubmit = useCallback(async () => {
    const trimmed = codeInput.trim();
    if (!trimmed) {
      setCodeError('Please enter your sign-in code');
      return;
    }

    setCodeLoading(true);
    setCodeError(null);

    try {
      const baseUrl = getApiBaseUrl();
      const res = await fetch(`${baseUrl}/api/auth/verify-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: trimmed }),
      });

      const data = await res.json();

      if (!res.ok) {
        setCodeError(data.error || 'Invalid code');
        return;
      }

      // Success — log in and close
      login(data.jwt, data.user, data.cfCookies);
      onSkip(); // Close login screen
    } catch {
      setCodeError('Network error. Please try again.');
    } finally {
      setCodeLoading(false);
    }
  }, [codeInput, login, onSkip]);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const trimmed = email.trim().toLowerCase();
    if (!trimmed) {
      setError('Please enter your email address');
      return;
    }

    setLoading(true);
    try {
      const baseUrl = getApiBaseUrl();
      const res = await fetch(`${baseUrl}/api/auth/request-magic-link`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: trimmed }),
      });

      const data = await res.json();

      if (!res.ok) {
        setError(data.error || 'Failed to send magic link');
        setStep('error');
        return;
      }

      setStep('sent');
    } catch {
      setError('Network error. Please try again.');
      setStep('error');
    } finally {
      setLoading(false);
    }
  }, [email]);

  return (
    <div className={styles.container}>
      <div className={styles.card}>
        <div className={styles.logo}>🔥</div>
        <h1 className={styles.title}>Talkyn</h1>

        {step === 'email' || step === 'error' ? (
          <>
            <p className={styles.subtitle}>
              Sign in with your email to unlock cloud features
            </p>

            <form onSubmit={handleSubmit} className={styles.form}>
              <input
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setError(null);
                  setStep('email');
                }}
                placeholder="you@example.com"
                className={styles.input}
                autoComplete="email"
                autoFocus
                disabled={loading}
                required
              />

              {error && <div className={styles.error}>{error}</div>}

              <button
                type="submit"
                className={styles.primaryBtn}
                disabled={loading || !email.trim()}
              >
                {loading ? 'Sending…' : 'Send magic link'}
              </button>
            </form>

            <div className={styles.divider}>
              <span>or</span>
            </div>

            <button
              type="button"
              className={styles.skipBtn}
              onClick={onSkip}
            >
              {skipLabel}
            </button>

            <p className={styles.hint}>
              No password needed — we'll send a sign-in link to your email.
            </p>
          </>
        ) : (
          /* step === 'sent' */
          <>
            <div className={styles.sentIcon}>📨</div>
            <p className={styles.subtitle}>Check your email</p>
            <p className={styles.sentText}>
              We sent a sign-in code to<br />
              <strong>{email.trim().toLowerCase()}</strong>
            </p>

            <div className={styles.form}>
              <input
                type="text"
                value={codeInput}
                onChange={(e) => {
                  setCodeInput(e.target.value.toUpperCase());
                  setCodeError(null);
                }}
                placeholder="XXXX-XXXX"
                className={styles.input}
                style={{ textAlign: 'center', letterSpacing: '2px', fontSize: '20px', fontFamily: 'monospace' }}
                autoComplete="one-time-code"
                autoFocus
                disabled={codeLoading}
                maxLength={10}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    void handleCodeSubmit();
                  }
                }}
              />

              {codeError && <div className={styles.error}>{codeError}</div>}

              <button
                type="button"
                className={styles.primaryBtn}
                onClick={() => void handleCodeSubmit()}
                disabled={codeLoading || !codeInput.trim()}
              >
                {codeLoading ? 'Verifying…' : 'Verify code'}
              </button>
            </div>

            <p className={styles.sentHint}>
              Or click the magic link in the email. The link expires in 15 minutes.
            </p>

            <button
              type="button"
              className={styles.secondaryBtn}
              onClick={() => {
                setStep('email');
                setError(null);
                setCodeInput('');
                setCodeError(null);
              }}
            >
              ← Try a different email
            </button>

            <button
              type="button"
              className={styles.skipBtn}
              onClick={onSkip}
            >
              {skipLabel}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
